import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { useAuth } from '@/providers/AuthProvider';
import { useFormErrors } from '@/hooks/useFormErrors';

/**
 * Registration — BUILD-PLAN §17, §46 (data consent captured at collection).
 *
 * The password rules shown here mirror the backend policy, but the backend
 * remains authoritative: this is guidance so the customer is not surprised,
 * not the enforcement point (§10).
 */
export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { fieldErrors, formError, handleError, reset } = useFormErrors();

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    accepts_terms: false,
    accepts_marketing: false,
  });

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    reset();
    setSubmitting(true);

    try {
      await register({
        name: form.name,
        email: form.email,
        // Omit rather than send an empty string, which would fail the
        // backend's phone format rule for a field the customer left blank.
        ...(form.phone ? { phone: form.phone } : {}),
        password: form.password,
        password_confirmation: form.password_confirmation,
        accepts_terms: form.accepts_terms,
        accepts_marketing: form.accepts_marketing,
      });

      navigate('/verify-email', { replace: true });
    } catch (error) {
      handleError(error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Create your account"
      description="Save your details, track orders and keep a wishlist of favourites."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="text-accent underline underline-offset-4">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        {formError && <Alert tone="error">{formError}</Alert>}

        <Input
          label="Full name"
          name="name"
          value={form.name}
          onChange={(event) => update('name', event.target.value)}
          error={fieldErrors['name']}
          autoComplete="name"
          required
        />

        <Input
          label="Email address"
          type="email"
          name="email"
          value={form.email}
          onChange={(event) => update('email', event.target.value)}
          error={fieldErrors['email']}
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          spellCheck={false}
          required
        />

        <Input
          label="Phone number"
          type="tel"
          name="phone"
          value={form.phone}
          onChange={(event) => update('phone', event.target.value)}
          error={fieldErrors['phone']}
          autoComplete="tel"
          inputMode="tel"
          hint="Optional — for delivery updates."
        />

        <Input
          label="Password"
          type="password"
          name="password"
          value={form.password}
          onChange={(event) => update('password', event.target.value)}
          error={fieldErrors['password']}
          autoComplete="new-password"
          hint="At least 12 characters, with upper and lower case letters and a number."
          required
        />

        <Input
          label="Confirm password"
          type="password"
          name="password_confirmation"
          value={form.password_confirmation}
          onChange={(event) => update('password_confirmation', event.target.value)}
          error={fieldErrors['password_confirmation']}
          autoComplete="new-password"
          required
        />

        <div className="flex flex-col gap-3">
          <label className="flex items-start gap-2.5 text-sm leading-relaxed text-muted">
            <input
              type="checkbox"
              checked={form.accepts_terms}
              onChange={(event) => update('accepts_terms', event.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
            />
            <span>
              I agree to the{' '}
              <Link to="/policies/terms" className="text-accent underline underline-offset-4">
                Terms &amp; Conditions
              </Link>{' '}
              and{' '}
              <Link to="/policies/privacy" className="text-accent underline underline-offset-4">
                Privacy Policy
              </Link>
              .
            </span>
          </label>

          {fieldErrors['accepts_terms'] && (
            <p role="alert" className="text-xs text-danger">
              {fieldErrors['accepts_terms']}
            </p>
          )}

          {/*
            §39/§46: marketing consent is separate from the terms checkbox and
            defaults to off. Bundling them would make the opt-in meaningless.
          */}
          <label className="flex items-start gap-2.5 text-sm leading-relaxed text-muted">
            <input
              type="checkbox"
              checked={form.accepts_marketing}
              onChange={(event) => update('accepts_marketing', event.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
            />
            <span>Send me news of new collections and occasions. You can unsubscribe anytime.</span>
          </label>
        </div>

        <Button type="submit" loading={submitting} fullWidth size="lg">
          Create Account
        </Button>
      </form>
    </AuthLayout>
  );
}
